import { AccountInterface, Nullable, PaymentModeId } from '../interfaces'

/**
 * Un cliente puede tener VARIAS líneas de facturación bajo el MISMO accountID
 * (p. ej. "2868 - DUNCAN" y "2870 - DUNCAN" comparten
 * accountID 49097221-…). `Account/GetAccount?accountId=` devuelve una sola vez
 * los datos de la cuenta y, en `listAuthorizingAccount`, una entrada por línea
 * con su propio `accountSiteID` / `accountSiteCode` / `agreementID`.
 *
 * El `agreementID` de la raíz corresponde a UNA sola de esas líneas, así que
 * usarlo directo hace que las demás consulten el contrato equivocado en
 * `/Agreement/AgreementBU?AgreementID=`. Estas funciones resuelven la entrada
 * que corresponde a la línea que el usuario seleccionó.
 */

/** 0, null o undefined significan "línea sin sitio" (la de COD/contado). */
const normalizeSiteId = (value?: Nullable<number | string>): string | null => {
  if (value === null || value === undefined) return null
  const id = String(value).trim()
  return id === '' || id === '0' ? null : id
}

const normalizeCode = (value?: Nullable<string | number>): string | null => {
  if (value === null || value === undefined) return null
  const code = String(value).trim()
  return code === '' ? null : code
}

const isCOD = (account: AccountInterface): boolean =>
  normalizeCode(account.paymentModeID) === PaymentModeId.COD.toString()

/**
 * Entrada de `listAuthorizingAccount` que corresponde a la línea seleccionada.
 * Busca por `accountSiteID`; si la línea no tiene sitio, toma la línea de
 * facturación COD (paymentModeID = 2). En ambos casos cae al `accountSiteCode`
 * como respaldo.
 */
export const resolveAccountSiteAuthorization = (
  account: Nullable<AccountInterface>,
  selectedSite: Nullable<Pick<AccountInterface, 'accountSiteID' | 'accountCode'>>
): AccountInterface | undefined => {
  const list = account?.listAuthorizingAccount ?? []
  if (list.length === 0) return undefined

  const siteId = normalizeSiteId(selectedSite?.accountSiteID)
  const code = normalizeCode(selectedSite?.accountCode)

  const byCode =
    code !== null
      ? list.find((a) => normalizeCode(a.accountSiteCode) === code)
      : undefined

  if (siteId !== null) {
    const byId = list.find((a) => normalizeSiteId(a.accountSiteID) === siteId)
    return byId ?? byCode
  }

  const byCOD = list.find(
    (a) => normalizeSiteId(a.accountSiteID) === null && isCOD(a)
  )
  return byCOD ?? byCode
}

/**
 * AgreementID de la línea seleccionada, con el de la raíz como último recurso
 * (cuentas sin `listAuthorizingAccount`, como las de una sola línea).
 */
export const resolveAgreementIDForSite = (
  account: Nullable<AccountInterface>,
  selectedSite: Nullable<Pick<AccountInterface, 'accountSiteID' | 'accountCode'>>
): string | undefined =>
  resolveAccountSiteAuthorization(account, selectedSite)?.agreementID ??
  account?.agreementID
